import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { handlePostHogIdentity } from '../utils/posthogUtils';
import { captureAppException } from '../utils/errorCaptureUtils';


// --- Helper Function: Basic Deep User Comparison ---
/**
 * Performs a basic deep comparison of two Supabase User objects.
 * Focuses on ID, email, role, and metadata.
 * NOTE: Uses JSON.stringify for metadata comparison, which has limitations
 * (key order, non-serializable values) but is often sufficient for typical metadata.
 */
const areUsersEqual = (user1: User | null, user2: User | null): boolean => {
  if (user1 === user2) return true; // Same reference or both null
  if (!user1 || !user2) return false; // One is null, the other isn't
  if (user1.id !== user2.id) return false; // Different users are never equal

  // Compare relevant primitive properties
  if (user1.email !== user2.email) return false;
  if (user1.role !== user2.role) return false;
  if (user1.aud !== user2.aud) return false;

  // Compare metadata using JSON stringify (basic deep check)
  try {
    const meta1 = JSON.stringify(user1.user_metadata || {});
    const meta2 = JSON.stringify(user2.user_metadata || {});
    if (meta1 !== meta2) return false;
  } catch (e) {
    console.error("Error comparing user metadata:", e);
    captureAppException(e);
    return false; // Treat comparison error as unequal
  }

  return true;
};

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUserMetadata: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Memoized functions
  const refreshUserMetadata = useCallback(async () => {
    // Fetch the updated user from Supabase
    const { data: res } = await supabase.auth.getUser();
    if (res?.user) {
      setUser(res.user);
      setSession(prevSession => prevSession ? { ...prevSession, user: res.user } : {user: res.user} as Session);
    }
  }, []); // supabase, setUser, and setSession are stable

  const signOut = useCallback(async () => {
    // Only reset if there was a previously identified user or if PostHog has an ID set
    handlePostHogIdentity(null);
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        // --- PostHog Identify on Initial Load ---
        handlePostHogIdentity(session.user);
        // --- End PostHog Identify ---
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (_, session) => {
      setSession(session);

      // --- START Explanation: Stabilizing User Object Reference ---
      // PROBLEM: Supabase client (and libraries like React Query/SWR)
      // often refresh the user session or refetch data automatically when
      // the browser window regains focus. The `onAuthStateChange` listener
      // receives a new `session` object during these refreshes.
      // If we unconditionally call `setUser(session?.user)`, we create a
      // *new object reference* for the `user` state, even if the actual
      // user data (ID, email, metadata) hasn't changed.
      //
      // CONSEQUENCE: Components using `useAuth()` often include `user` in
      // their `useEffect` dependency arrays (e.g., to fetch data specific
      // to that user). When the `user` object reference changes (even if
      // the content is the same), these effects re-run unnecessarily.
      // This can lead to:
      //   - Disruptive UI flashes (e.g., showing loading states briefly).
      //   - Unnecessary backend requests (re-fetching data).
      //   - Potential state loss in child components if the re-render is severe.
      //
      // SOLUTION: We perform a deep comparison between the `currentUser`
      // state and the incoming `session?.user`. We only call `setUser`
      // to update the state if the user data has *actually* changed.
      // If the data is the same, we return the `currentUser` reference,
      // preventing the state update and the subsequent chain reaction of
      // effect re-runs in dependent components.
      // --- END Explanation --- 
      setUser(currentUser => {
        const newUser = session?.user ?? null;
        // Use the deep comparison helper
        if (!areUsersEqual(currentUser, newUser)) {
          // --- PostHog Identify/Reset on Auth State Change ---
          handlePostHogIdentity(newUser);
          // --- End PostHog Identify/Reset ---
          return newUser; // Data is different, update state
        }
        return currentUser; // Data is the same, return current state reference
      });
    });

    if (user?.id) {
      // Create channel with specific name for workspace user changes
      const channel = supabase
        .channel('workspace_user_changes', {
          config: {
            broadcast: {
              self: true
            }
          }
        })
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'workspace_users',
            filter: `user_id=eq.${user.id}`,
          },
          async () => {
            await refreshUserMetadata();
          }
        );

      // Start listening to the channel
      channel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('Subscribed to workspace user changes');
        }
      });
    }

    // Cleanup listener on component unmount
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []); // Reduced dependency array - only runs on mount/unmount

  // Important WARNING: Context value stability is crucial here. Each time the context value changes (new object reference),
  // all consuming components will re-render, potentially causing cascading updates and infinite loops.
  // We use useCallback for functions (signOut, refreshUserMetadata) to maintain stable references.
  // The primitive values (session, user, loading) are already stable as they come from useState.
  // This is particularly important because many components depend on this context for auth state and
  // may trigger additional auth-related operations in their effects.
  return (
    <AuthContext.Provider value={{ session, user, loading, signOut, refreshUserMetadata }}>
      {children}
    </AuthContext.Provider>
  );
};